import type { InstantConfigRepository } from "instant-config";
import { PrebidBidderAdapter } from "./PrebidBidderAdapter";
import { AppNexusBidderAdapter } from "./AppNexusBidderAdapter";
import { OzoneBidderAdapter } from "./OzoneBidderAdapter";
import { RubiconBidderAdapter } from "./RubiconBidderAdapter";

/**
 * Mapping of Prebid bidder names where ICBM config variable name doesn't match
 * the format of `icPrebid${BidderName}`.
 */
type BidderNameMapping = {
  appnexus: "icPrebidAppNexus";
  gumgum: "icPrebidGumGum";
  ix: "icPrebidIX";
  mgnipbs: "icPrebidMagniteS2s";
  relevantdigital: "icPrebidWebAds";
  openx: "icPrebidOpenX";
};

/**
 * Type for Prebid icPrebid<BidderName> ICBM variable names used in ICBM
 * configuration.
 */
export type BidderConfigFlags<T extends PrebidBidderName> =
  T extends keyof BidderNameMapping
    ? BidderNameMapping[T]
    : `icPrebid${Capitalize<T>}`;

export type PrebidAdapterConfig = Pick<
  InstantConfigRepository,
  | BidderConfigFlags<Exclude<PrebidBidderName, "wikia">>
  | "icFeaturedVideoPlayer"
>;

export type BidderAdaptersMap = {
  [K in PrebidBidderName]: PrebidBidderAdapter<K>;
};

/**
 * Mapping of bidder name to the PrebidBidderAdapter object. This mapping
 * defines all possible bidders, and the PrebidPlugin will loop through the
 * mapping to find out which bidders are enabled in the current configuration
 * (using adapter.isEnabled()).
 *
 * During the bidding process, each active adapter will be called to allow the
 * adapter to transform the ad unit bids before they are sent to Prebid.js
 * auction.  All `PrebidBidderAdapter` instances will use just return the
 * bidder config as is, but any custom bidder adapters can override the
 * `transformAdUnitBidConfigs` method to transform the ad unit bids for that
 * specific bidder.
 */
export const bidderAdapters: BidderAdaptersMap = {
  appnexus: new AppNexusBidderAdapter("icPrebidAppNexus"),
  gumgum: new PrebidBidderAdapter<"gumgum">("icPrebidGumGum"),
  ix: new PrebidBidderAdapter<"ix">("icPrebidIX"),
  kargo: new PrebidBidderAdapter<"kargo">("icPrebidKargo"),
  mgnipbs: new PrebidBidderAdapter<"mgnipbs">("icPrebidMagniteS2s"),
  medianet: new PrebidBidderAdapter<"medianet">("icPrebidMedianet"),
  nobid: new PrebidBidderAdapter<"nobid">("icPrebidNobid"),
  openx: new PrebidBidderAdapter<"openx">("icPrebidOpenX"),
  ozone: new OzoneBidderAdapter("icPrebidOzone"),
  pubmatic: new PrebidBidderAdapter<"pubmatic">("icPrebidPubmatic"),
  rubicon: new RubiconBidderAdapter("icPrebidRubicon"),
  seedtag: new PrebidBidderAdapter<"seedtag">("icPrebidSeedtag"),
  triplelift: new PrebidBidderAdapter<"triplelift">("icPrebidTriplelift"),
  wikia: new PrebidBidderAdapter<"wikia">(),
};
